import { Box, Paper, Stack, Typography } from '@mui/material';
import { AgChartOptions } from 'ag-charts-enterprise';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { truckModeEnum } from '../../../../constants';
import { useTrucksSupervisionQuery } from '../../../../hooks/queries/useTrucksQuery';
import { formatNumber } from '../../../../utils/numbers';
import GenericAgChart from '../../../charts/GenericAgChart';
import TruckCardSupervision from '../../../domain/TruckCardSupervision';
import ScrollPageContainer from '../../../layout/ScrollPageContainer';

interface TrucksLiveSupervisionProps {
    date: Date;
}

const TrucksLiveSupervision: React.FC<TrucksLiveSupervisionProps> = ({ date }) => {
    const { t } = useTranslation();
    const {
        data: dataTrucksSupervision,
        isLoading: isLoadingTrucksSupervision,
        isError: isErrorTrucksSupervision,
    } = useTrucksSupervisionQuery({ date: date.toISOString() });

    const trucksSupervision = dataTrucksSupervision?.sort((a, b) => {
        const orderA = a.truck.truckTypeId.displayOrder || Infinity;
        const orderB = b.truck.truckTypeId.displayOrder || Infinity;
        if (orderA !== orderB) {
            return orderA - orderB;
        }

        const designationA = a.truck?.designation || '';
        const designationB = b.truck?.designation || '';
        return designationA.localeCompare(designationB);
    });

    const summary = useMemo(() => {
        if (!trucksSupervision) {
            return { totalRides: 0, totalDeparture: 0, totalArrival: 0 };
        }

        const totalRides = trucksSupervision.reduce((acc, truck) => acc + truck.rides.length, 0);
        const totalRidesAppro = trucksSupervision.reduce((acc, truck) => acc + truck.rides.filter((r) => r.mode === truckModeEnum.APPRO).length, 0);
        const totalRidesEvac = trucksSupervision.reduce((acc, truck) => acc + truck.rides.filter((r) => r.mode === truckModeEnum.EVAC).length, 0);

        const totalDeparture = trucksSupervision.reduce((acc, truck) => acc + truck.rides.reduce((sum, r) => sum + (r.departureTonnage || 0), 0), 0);

        const totalArrival = trucksSupervision.reduce((acc, truck) => acc + truck.rides.reduce((sum, r) => sum + (r.arrivalTonnage || 0), 0), 0);

        return { totalRides, totalRidesAppro, totalRidesEvac, totalDeparture, totalArrival };
    }, [trucksSupervision]);

    const chartOptions = useMemo((): AgChartOptions | null => {
        if (!trucksSupervision || trucksSupervision.length === 0) {
            return null;
        }

        const chartData = trucksSupervision.map((truck) => {
            const totalDeparture = truck.rides.reduce((acc, r) => acc + (r.departureTonnage || 0), 0);
            const totalArrival = truck.rides.reduce((acc, r) => acc + (r.arrivalTonnage || 0), 0);

            return {
                truck: truck.truck?.plateNumber || truck.truck?.designation || 'N/A',
                appro: Math.round(totalDeparture * 100) / 100,
                evac: Math.round(totalArrival * 100) / 100,
            };
        });

        return {
            data: chartData,
            series: [
                {
                    type: 'bar',
                    xKey: 'truck',
                    yKey: 'appro',
                    yName: 'Tonnage Appro',
                    fill: '#00b8c2',
                    strokeWidth: 0,
                },
                {
                    type: 'bar',
                    xKey: 'truck',
                    yKey: 'evac',
                    yName: 'Tonnage Évac',
                    fill: 'lightgreen',
                    strokeWidth: 0,
                },
            ],
            axes: [
                {
                    type: 'category',
                    position: 'bottom',
                },
                {
                    type: 'number',
                    position: 'left',
                    label: {
                        formatter: ({ value }) => `${value} t`,
                    },
                },
            ],
        };
    }, [trucksSupervision]);

    return (
        <ScrollPageContainer loading={isLoadingTrucksSupervision}>
            {trucksSupervision && (
                <Paper sx={{ p: 2, gap: 3, display: 'flex', flexDirection: 'column', mb: 1 }}>
                    <Stack direction="row" justifyContent="space-around">
                        <Box textAlign="center">
                            <Typography variant="h4" color="primary">
                                {summary.totalRides}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Tours
                            </Typography>
                        </Box>

                        <Box textAlign="center">
                            <Typography variant="h4" color="primary">
                                {summary.totalRidesAppro}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Appro
                            </Typography>
                        </Box>
                        <Box textAlign="center">
                            <Typography variant="h4" sx={{ color: '#00b8c2' }}>
                                {formatNumber(summary.totalDeparture, t('locale'), 2)} t
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Tonnage Appro
                            </Typography>
                        </Box>
                        <Box textAlign="center">
                            <Typography variant="h4" color="primary">
                                {summary.totalRidesEvac}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Evac
                            </Typography>
                        </Box>
                        <Box textAlign="center">
                            <Typography variant="h4" sx={{ color: 'lightgreen' }}>
                                {formatNumber(summary.totalArrival, t('locale'), 2)} t
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Tonnage Évac
                            </Typography>
                        </Box>
                    </Stack>
                    <GenericAgChart options={chartOptions} />
                </Paper>
            )}

            {trucksSupervision?.map((truck) => (
                <TruckCardSupervision key={truck.truck._id} truck={truck} />
            ))}
        </ScrollPageContainer>
    );
};

export default TrucksLiveSupervision;
